<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { AlertCircle, CheckCircle, Info } from 'lucide-vue-next'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import { Checkbox } from '@/components/ui/checkbox'
  import { Button } from '@/components/ui/button'
  import InformationalBanner from '@/components/ui/informational-banner/InformationalBanner.vue'
  import { useToast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables'
  import {
    useAiAssistantBeta,
    type DeactivationReason,
    type FeatureId
  } from '@/components/agent-actions/ai-assistant-beta/useAiAssistantBeta'
  import AiAssistantBetaDeactivateDialog from '@/components/agent-actions/ai-assistant-beta/AiAssistantBetaDeactivateDialog.vue'

  // Defense-in-depth FF gate: route is registered unconditionally, so a direct URL hit with
  // the flag off would otherwise expose the activation card. The template root v-ifs on
  // isFeatureEnabled — same pattern as SidePanelTabBar / AiAssistantBetaSidePanelView.
  const {
    isAdmin,
    isActivated,
    isFeatureEnabled,
    isLoading,
    activatedFeatures,
    activate,
    setActivatedFeature,
    deactivate
  } = useAiAssistantBeta()
  const { toast } = useToast()

  const { t } = useTranslation({
    en: {
      pageTitle: 'AI Assistant (Beta)',
      adminOnly: 'Only admin users can change AI assistant settings',
      body: 'Let JUPUS handle the work for you — autonomously. The assistant beta can execute a limited number of tasks on its own or draft your first email autonomously. All suggestions are generated based on your past interactions on our platform.',
      skillsHeader: 'Please select the skills you want to activate',
      changesApplyFuture:
        'Changes will be applied to future inquiries and not affect the existing ones',
      skillAssignLawFields: 'Assign law fields',
      skillAssignClerks: 'Assign clerks',
      skillSuggestEmail: 'Suggest the first client email',
      disclaimer:
        'This is a beta release so AI can make mistakes. Free for a limited time.\nYou can change selected features or opt out anytime.',
      activate: 'Activate AI assistant',
      deactivate: 'Deactivate AI assistant',
      // Toast strings — only fired when the beta is already activated.
      toastEnabled: 'AI assistant will automatically {action} for future inquiries',
      toastDisabled: 'AI assistant will not {action} for future inquiries',
      actionAssignLawFields: 'assign law fields',
      actionAssignClerks: 'assign clerks',
      actionSuggestEmail: 'suggest the first client emails',
      errorActivate: "Couldn't activate the AI assistant. Please try again.",
      errorSetSkill: "Couldn't update the skill. Please try again.",
      errorDeactivate: "Couldn't deactivate the AI assistant. Please try again."
    },
    de: {
      pageTitle: 'KI-Assistent (Beta)',
      adminOnly: 'Nur Administratoren können die AI-Assistant-Einstellungen ändern',
      body: 'Lass JUPUS die Arbeit für dich erledigen — autonom. Die Assistant-Beta kann eine begrenzte Anzahl von Aufgaben eigenständig ausführen oder deine erste E-Mail autonom entwerfen. Alle Vorschläge basieren auf deinen bisherigen Interaktionen auf unserer Plattform.',
      skillsHeader: 'Bitte wähle die Funktionen aus, die du aktivieren möchtest',
      changesApplyFuture:
        'Änderungen werden auf zukünftige Anfragen angewendet und betreffen bestehende nicht',
      skillAssignLawFields: 'Rechtsgebiete zuweisen',
      skillAssignClerks: 'Sachbearbeiter zuweisen',
      skillSuggestEmail: 'Erste Mandanten-E-Mail vorschlagen',
      disclaimer:
        'Dies ist eine Beta-Version, daher kann die KI Fehler machen. Für begrenzte Zeit kostenlos.\nDu kannst die ausgewählten Funktionen jederzeit ändern oder dich abmelden.',
      activate: 'AI Assistant aktivieren',
      deactivate: 'AI Assistant deaktivieren',
      toastEnabled: 'AI Assistant wird automatisch {action} für zukünftige Anfragen',
      toastDisabled: 'AI Assistant wird nicht {action} für zukünftige Anfragen',
      actionAssignLawFields: 'Rechtsgebiete zuweisen',
      actionAssignClerks: 'Sachbearbeiter zuweisen',
      actionSuggestEmail: 'erste Mandanten-E-Mails vorschlagen',
      errorActivate: 'AI Assistant konnte nicht aktiviert werden. Bitte versuche es erneut.',
      errorSetSkill: 'Funktion konnte nicht aktualisiert werden. Bitte versuche es erneut.',
      errorDeactivate: 'AI Assistant konnte nicht deaktiviert werden. Bitte versuche es erneut.'
    }
  })

  const skills: ReadonlyArray<{
    id: FeatureId
    labelKey: 'skillAssignLawFields' | 'skillAssignClerks' | 'skillSuggestEmail'
    actionKey: 'actionAssignLawFields' | 'actionAssignClerks' | 'actionSuggestEmail'
  }> = [
    {
      id: 'assign_law_fields',
      labelKey: 'skillAssignLawFields',
      actionKey: 'actionAssignLawFields'
    },
    { id: 'assign_clerks', labelKey: 'skillAssignClerks', actionKey: 'actionAssignClerks' },
    {
      id: 'suggest_first_client_email',
      labelKey: 'skillSuggestEmail',
      actionKey: 'actionSuggestEmail'
    }
  ]

  // Pre-activation working set — gets handed to activate() when the admin clicks Activate.
  // Once the org is activated, the source of truth is `activatedFeatures` from the composable.
  const localSelected = ref<FeatureId[]>(skills.map((s) => s.id))

  // Reset the pre-activation selection back to all-three whenever we leave the activated state,
  // so re-activating after a deactivation starts from the same default the modal would show.
  watch(isActivated, (active) => {
    if (!active) localSelected.value = skills.map((s) => s.id)
  })

  const selectedFeatures = computed<readonly FeatureId[]>(() =>
    isActivated.value ? activatedFeatures.value : localSelected.value
  )

  const canActivate = computed(
    () => isAdmin.value && !isActivated.value && !isLoading.value && localSelected.value.length > 0
  )

  function isSelected(id: FeatureId) {
    return selectedFeatures.value.includes(id)
  }

  function isCheckboxDisabled(id: FeatureId) {
    if (!isAdmin.value) return true
    // Disable while a request is in flight so the user can't fire a second toggle that
    // races the first.
    if (isLoading.value) return true
    // Last-locked: keep at least one feature selected.
    if (selectedFeatures.value.length === 1 && isSelected(id)) return true
    return false
  }

  function showErrorToast(titleKey: 'errorActivate' | 'errorSetSkill' | 'errorDeactivate') {
    toast({
      variant: 'destructive',
      icon: AlertCircle,
      title: t(titleKey)
    })
  }

  async function onToggle(id: FeatureId, checked: boolean) {
    if (isActivated.value) {
      try {
        await setActivatedFeature(id, checked)
      } catch {
        showErrorToast('errorSetSkill')
        return
      }
      // Only fire the success toast after the backend confirms — otherwise an error path
      // would leave the user looking at "skill enabled" while the server still rejects it.
      const skill = skills.find((s) => s.id === id)
      if (skill) {
        toast({
          variant: 'neutralSuccess',
          icon: CheckCircle,
          iconClass: 'text-[#059669]',
          title: t(checked ? 'toastEnabled' : 'toastDisabled', {
            action: t(skill.actionKey)
          })
        })
      }
      return
    }

    // Pre-activation: local working set, no toasts (per spec).
    if (checked) {
      if (!localSelected.value.includes(id)) {
        localSelected.value = [...localSelected.value, id]
      }
    } else {
      localSelected.value = localSelected.value.filter((f) => f !== id)
    }
  }

  async function handleActivate() {
    if (!canActivate.value) return
    try {
      await activate([...localSelected.value])
    } catch {
      showErrorToast('errorActivate')
    }
  }

  const showDeactivateDialog = ref(false)

  function openDeactivateDialog() {
    if (!isAdmin.value || !isActivated.value || isLoading.value) return
    showDeactivateDialog.value = true
  }

  async function handleDeactivateConfirmed(reason: DeactivationReason, feedback: string) {
    try {
      await deactivate(reason, feedback)
    } catch {
      showErrorToast('errorDeactivate')
    }
  }
</script>

<template>
  <SettingsPage v-if="isFeatureEnabled">
    <template #actions></template>

    <div class="space-y-6 p-6">
      <InformationalBanner
        v-if="!isAdmin"
        :icon="Info"
        class="self-stretch"
      >
        {{ t('adminOnly') }}
      </InformationalBanner>

      <p
        v-if="!isActivated"
        class="self-stretch text-sm font-normal leading-5 text-[#1F2937]"
      >
        {{ t('body') }}
      </p>

      <div class="flex flex-col items-start gap-2 self-stretch">
        <div class="flex flex-col self-stretch">
          <h2 class="text-sm font-semibold leading-5 text-[#1F2937]">
            {{ t('skillsHeader') }}
          </h2>
          <p
            v-if="isActivated"
            class="text-xs font-normal leading-4 text-[#6B7280]"
          >
            {{ t('changesApplyFuture') }}
          </p>
        </div>
        <div class="flex flex-col items-start gap-1 self-stretch">
          <label
            v-for="skill in skills"
            :key="skill.id"
            class="inline-flex items-center gap-2"
            :class="isCheckboxDisabled(skill.id) ? 'cursor-not-allowed' : 'cursor-pointer'"
          >
            <Checkbox
              :checked="isSelected(skill.id)"
              :disabled="isCheckboxDisabled(skill.id)"
              @update:checked="(v: boolean) => onToggle(skill.id, v)"
            />
            <span class="text-sm font-normal leading-5 text-[#1F2937]">
              {{ t(skill.labelKey) }}
            </span>
          </label>
        </div>
      </div>

      <p class="whitespace-pre-line text-xs font-normal leading-4 text-[#6B7280]">
        {{ t('disclaimer') }}
      </p>

      <Button
        v-if="!isActivated"
        :disabled="!canActivate"
        class="h-10 rounded-lg px-4 text-base font-medium leading-6 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
        @click="handleActivate"
      >
        {{ t('activate') }}
      </Button>
      <button
        v-else
        type="button"
        :disabled="!isAdmin || isLoading"
        class="text-sm font-medium leading-5 text-destructive transition-colors hover:underline
          disabled:cursor-not-allowed disabled:opacity-50"
        @click="openDeactivateDialog"
      >
        {{ t('deactivate') }}
      </button>
    </div>

    <AiAssistantBetaDeactivateDialog
      v-model:open="showDeactivateDialog"
      @confirm="handleDeactivateConfirmed"
    />
  </SettingsPage>
</template>
